import { api } from "@/shared/lib/api";

import { ApiBracketSchema } from "./schema";
import type { ApiBracketDTO, ApiMatchPendingResult } from "./types";
import { translateUi } from "@/shared/lib/ui-translate";

interface LegacyApiTeam {
  id: string | null;
  name: string;
  tag?: string;
  logo?: string | null;
  type: string;
}

interface LegacyApiMatch {
  id: string;
  name?: string;
  round: number;
  matchNumber?: number;
  position?: number;
  bracketSection?: string;
  sectionRound?: number;
  nextMatchIds?: string[];
  updatedAt?: string;
  bestOf?: 1 | 3 | 5;
  team1: LegacyApiTeam | null;
  team2: LegacyApiTeam | null;
  winner?: LegacyApiTeam | null;
  team1Score?: number;
  team2Score?: number;
  status?: string;
  phase?: string;
  scheduledTime?: string;
  startTime?: string | null;
  startByDeadlineUtc?: string | null;
  isTechnicalWin?: boolean;
  /**
   * Есть ли у завершённого матча игровая статистика. null — матч ещё не завершён
   * либо это тех. победа (игры не было). false означает «счёт есть, статистики нет»:
   * матч закрыли согласием капитанов, реплей так и не приехал.
   */
  hasStats?: boolean | null;
  pausedAtUtc?: string | null;
  completedAt?: string | null;
  tournamentCode?: string;
  team1PausesUsed?: number;
  team2PausesUsed?: number;
  team1PausesRemaining?: number;
  team2PausesRemaining?: number;
  pausedByTeamId?: string | null;
  pausesAllowedPerMatch?: number;
  pendingResult?: ApiMatchPendingResult | null;
  preDraftRoomId?: string | null;
  preDraftBlueLink?: string | null;
  preDraftRedLink?: string | null;
  preDraftSpectatorLink?: string | null;
}

interface LegacyApiBracket {
  bracketId?: string;
  id?: string;
  tournamentId: string;
  type: string;
  status?: string;
  createdAt?: string;
  totalRounds?: number;
  currentRound?: number;
  matchStartDeadlineEnabled?: boolean;
  pausesAllowedPerMatch?: number;
  pauseDurationMinutes?: number;
  matches: LegacyApiMatch[];
  seedOrder?: string[] | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isLegacyApiBracket(value: unknown): value is LegacyApiBracket {
  if (!isRecord(value) || !Array.isArray(value["matches"])) return false;
  return value["matches"].every(
    (match) =>
      isRecord(match) &&
      typeof match["id"] === "string" &&
      match["id"].trim().length > 0,
  );
}

function normalizeBracketSection(
  raw: unknown,
): "winners" | "losers" | "finals" | undefined {
  if (raw === null || raw === undefined) return undefined;
  const text = String(raw).toLowerCase();
  if (
    text.includes("winner") ||
    text.includes("upper") ||
    text.includes("wb")
  ) {
    return "winners";
  }
  if (
    text.includes("loser") ||
    text.includes("lower") ||
    text.includes("lb")
  ) {
    return "losers";
  }
  if (text.includes("final")) {
    return "finals";
  }
  return undefined;
}

function normalizeBracketResponse(data: unknown): ApiBracketDTO {
  if (!isLegacyApiBracket(data)) {
    throw new Error("Invalid bracket response: missing bracket matches");
  }
  const legacy = data;

  const formatMap: Record<string, ApiBracketDTO["format"]> = {
    SingleElimination: "single_elimination",
    DoubleElimination: "double_elimination",
    SwissSystem: "swiss",
    RoundRobin: "round_robin",
  };

  const formatCandidate =
    formatMap[legacy.type] ??
    (legacy.type
      ?.replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase());
  if (
    formatCandidate !== "single_elimination" &&
    formatCandidate !== "double_elimination" &&
    formatCandidate !== "swiss" &&
    formatCandidate !== "round_robin"
  ) {
    throw new Error(`Unsupported bracket format: ${String(legacy.type)}`);
  }
  const format: ApiBracketDTO["format"] = formatCandidate;

  const teamsMap = new Map<string, ApiBracketDTO["teams"][0]>();
  const ensureTeam = (team: LegacyApiTeam | null | undefined) => {
    if (!team?.id) return;
    if (teamsMap.has(team.id)) return;

    const safeName =
      team.name?.trim() || team.tag?.trim() || translateUi("litClient.komandaN", { n: teamsMap.size + 1 });

    // Only set avatarUrl if logo is a non-empty string
    const avatarUrl = team.logo && team.logo.trim() ? team.logo : undefined;

    teamsMap.set(team.id, {
      id: team.id,
      name: safeName,
      ...(avatarUrl ? { avatarUrl } : {}),
    });
  };

  legacy.matches.forEach((match) => {
    if (match.team1?.id) {
      ensureTeam(match.team1);
    }
    if (match.team2?.id) {
      ensureTeam(match.team2);
    }
  });

  const teams = Array.from(teamsMap.values());

  const roundBuckets = new Map<number, LegacyApiMatch[]>();
  legacy.matches.forEach((match) => {
    const round = Number.isFinite(match.round) ? match.round : 0;
    const bucket = roundBuckets.get(round) ?? [];
    bucket.push(match);
    roundBuckets.set(round, bucket);
  });

  const orderedRounds = [...roundBuckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([roundKey, matches]) => ({
      roundKey,
      matches: matches.slice().sort((a, b) => {
        const posA = a.position ?? a.matchNumber ?? 0;
        const posB = b.position ?? b.matchNumber ?? 0;
        if (posA !== posB) return posA - posB;
        return a.id.localeCompare(b.id);
      }),
    }));

  const roundMeta = new Map<
    string,
    { roundIndex: number; positionIndex: number; roundKey: number }
  >();
  orderedRounds.forEach((bucket, roundIndex) => {
    bucket.matches.forEach((match, positionIndex) => {
      roundMeta.set(match.id, { roundIndex, positionIndex, roundKey: bucket.roundKey });
    });
  });

  const resolveRoundLabel = (roundIndex: number, totalRounds: number) => {
    const roundsToFinal = totalRounds - roundIndex;
    if (roundsToFinal === 1) return translateUi("litClient.final");
    if (roundsToFinal === 2) return translateUi("litClient.polufinal");
    if (roundsToFinal === 3) return translateUi("litClient.chetvertfinal");
    return translateUi("litClient.raundN", { n: roundIndex + 1 });
  };

  // KAN-1648: глобальный `round` у double elimination НЕ уникален между секциями —
  // бэкенд нумерует WB и LB каждую с 1, а гранд-финал ставит Round = W+L+1. Стадия
  // по длине объединённых бакетов называла полуфинал WB четвертьфиналом (DE4: WB R1
  // и LB R1 попадают в один бакет → roundsToFinal = 3). Стадия DE считается внутри
  // секции по sectionRound.
  const sectionRoundSets: Record<"winners" | "losers", Set<number>> = {
    winners: new Set<number>(),
    losers: new Set<number>(),
  };
  for (const m of legacy.matches) {
    const sr =
      typeof m.sectionRound === "number" && m.sectionRound > 0
        ? m.sectionRound
        : null;
    if (sr == null) continue;
    const sec =
      normalizeBracketSection(m.bracketSection) ??
      (format === "double_elimination"
        ? m.position === 9999
          ? "finals"
          : typeof m.position === "number" && m.position >= 1000
            ? "losers"
            : "winners"
        : undefined);
    if (sec === "winners" || sec === "losers") sectionRoundSets[sec].add(sr);
  }

  // Стадия матча внутри секции DE. null — секция не определена (SE, swiss) или у
  // матча нет sectionRound (старые данные): тогда считаем по старой глобальной схеме.
  const resolveSectionRoundLabel = (
    section: "winners" | "losers" | "finals" | undefined,
    sectionRound: number | null,
  ): string | null => {
    if (!section || sectionRound == null) return null;
    if (section === "finals") return translateUi("litClient.final");
    const total = sectionRoundSets[section].size;
    if (total === 0) return null;
    if (section === "winners") {
      const roundsToFinal = total - sectionRound + 1;
      if (roundsToFinal === 1) return translateUi("litClient.final");
      if (roundsToFinal === 2) return translateUi("litClient.polufinal");
      if (roundsToFinal === 3) return translateUi("litClient.chetvertfinal");
      return translateUi("litClient.raundN", { n: sectionRound });
    }
    // losers: последний раунд нижней сетки решает, кто идёт в гранд-финал.
    return sectionRound >= total
      ? translateUi("litClient.final")
      : translateUi("litClient.raundN", { n: sectionRound });
  };

  const matches: ApiBracketDTO["matches"] = legacy.matches.map((match) => {
    const meta = roundMeta.get(match.id) ?? { roundIndex: 0, positionIndex: 0, roundKey: 0 };
    const nextRoundBucket = orderedRounds[meta.roundIndex + 1];
    const nextMatchCandidate =
      nextRoundBucket?.matches[Math.floor(meta.positionIndex / 2)];

    const isSwissOrRR = format === "swiss" || format === "round_robin";
    const normalizedBracketSection = normalizeBracketSection(
      match.bracketSection,
    );
    const bracket =
      normalizedBracketSection ??
      (format === "double_elimination"
        ? match.position === 9999
          ? "finals"
          : typeof match.position === "number" && match.position >= 1000
            ? "losers"
            : "winners"
        : undefined);

    const roundIndex =
      typeof match.sectionRound === "number" && match.sectionRound > 0
        ? match.sectionRound - 1
        : meta.roundIndex;

    const roundLabel = isSwissOrRR
      ? translateUi("litClient.raundN", { n: Math.max(1, meta.roundKey) })
      : resolveSectionRoundLabel(
            bracket,
            typeof match.sectionRound === "number" && match.sectionRound > 0
              ? match.sectionRound
              : null,
          ) ?? resolveRoundLabel(meta.roundIndex, orderedRounds.length);

    const friendlyName =
      match.name ??
      translateUi("litClient.raundMatch", { round: roundLabel, n: meta.positionIndex + 1 });

    const serverNextMatchIds =
      Array.isArray(match.nextMatchIds) && match.nextMatchIds.length > 0
        ? match.nextMatchIds
        : undefined;

    return {
      id: match.id,
      name: friendlyName,
      matchNumber: match.matchNumber ?? meta.positionIndex + 1,
      roundIndex,
      ...(bracket ? { bracket } : {}),
      ...(match.bestOf ? { bestOf: match.bestOf } : {}),
      teamAId: match.team1?.id ?? undefined,
      teamBId: match.team2?.id ?? undefined,
      scoreA: match.team1Score,
      scoreB: match.team2Score,
      nextMatchIds: isSwissOrRR
        ? undefined
        : serverNextMatchIds ?? (nextMatchCandidate ? [nextMatchCandidate.id] : undefined),
      status: match.status,
      ...(match.phase ? { phase: match.phase } : {}),
      ...(match.updatedAt ? { updatedAt: match.updatedAt } : {}),
      ...(match.startByDeadlineUtc != null ? { startByDeadlineUtc: match.startByDeadlineUtc } : {}),
      ...(match.isTechnicalWin ? { isTechnicalWin: true } : {}),
      ...(match.hasStats != null ? { hasStats: match.hasStats } : {}),
      ...(match.team1PausesUsed !== undefined ? { team1PausesUsed: match.team1PausesUsed } : {}),
      ...(match.team2PausesUsed !== undefined ? { team2PausesUsed: match.team2PausesUsed } : {}),
      ...(match.team1PausesRemaining !== undefined ? { team1PausesRemaining: match.team1PausesRemaining } : {}),
      ...(match.team2PausesRemaining !== undefined ? { team2PausesRemaining: match.team2PausesRemaining } : {}),
      ...(match.pausedByTeamId != null ? { pausedByTeamId: match.pausedByTeamId } : {}),
      ...(match.pausesAllowedPerMatch !== undefined ? { pausesAllowedPerMatch: match.pausesAllowedPerMatch } : {}),
      ...(match.pendingResult ? { pendingResult: match.pendingResult } : {}),
      ...(match.preDraftRoomId != null
        ? { preDraftRoomId: match.preDraftRoomId }
        : {}),
      ...(match.preDraftBlueLink != null
        ? { preDraftBlueLink: match.preDraftBlueLink }
        : {}),
      ...(match.preDraftRedLink != null
        ? { preDraftRedLink: match.preDraftRedLink }
        : {}),
      ...(match.preDraftSpectatorLink != null
        ? { preDraftSpectatorLink: match.preDraftSpectatorLink }
        : {}),
    };
  });

  const pausesAllowedPerMatch = legacy.pausesAllowedPerMatch;
  const pauseDurationMinutes = legacy.pauseDurationMinutes;

  return {
    id: legacy.bracketId ?? legacy.id ?? "",
    format,
    status: legacy.status,
    teams,
    matches,
    ...(Array.isArray(legacy.seedOrder) ? { seedOrder: legacy.seedOrder } : {}),
    ...(legacy.matchStartDeadlineEnabled != null
      ? { matchStartDeadlineEnabled: legacy.matchStartDeadlineEnabled }
      : {}),
    ...(pausesAllowedPerMatch !== undefined ? { pausesAllowedPerMatch: pausesAllowedPerMatch } : {}),
    ...(pauseDurationMinutes !== undefined ? { pauseDurationMinutes: pauseDurationMinutes } : {}),
  };
}

export async function fetchBracketByTournamentId(
  tournamentId: string | undefined,
): Promise<ApiBracketDTO> {
  const normalizedTournamentId = tournamentId?.trim();
  if (!normalizedTournamentId) {
    throw new Error("TournamentId is required to load the bracket");
  }
  const { data } = await api.get<unknown>(
    `/brackets/${encodeURIComponent(normalizedTournamentId)}`,
  );

  const normalized = normalizeBracketResponse(data);

  const parsed = ApiBracketSchema.safeParse(normalized);
  if (!parsed.success) {
    console.warn(
      "[API] Invalid bracket response after normalization",
      parsed.error.format(),
    );
    console.warn("[API] Raw data:", data);
    console.warn("[API] Normalized data:", normalized);
    throw new Error("Invalid bracket response");
  }

  const final: ApiBracketDTO = {
    ...parsed.data,
    teams: parsed.data.teams.map((t) => ({
      ...t,
      avatarUrl: t.avatarUrl ?? "/avatars/default-avatar.svg",
    })),
  };

  return final;
}

export async function updateMatchBestOf(
  matchId: string,
  payload: { bestOf: 1 | 3 | 5; expectedUpdatedAt: string },
) {
  const { data } = await api.put(`/brackets/matches/${matchId}/best-of`, payload);
  return data;
}

export interface UpdateMatchScoreRequest {
  scoreA?: number;
  scoreB?: number;
  winnerId?: string;
}

export async function updateMatchScore(
  _tournamentId: string | undefined,
  matchId: string | undefined,
  payload: UpdateMatchScoreRequest,
): Promise<void> {
  if (!matchId) throw new Error("MatchId обязателен для обновления результата");
  const winnerId = payload.winnerId;
  if (!winnerId) throw new Error("WinnerId обязателен для фиксации результата");

  await api.put(`/brackets/matches/${matchId}/result`, {
    team1Score: Number(payload.scoreA ?? 0),
    team2Score: Number(payload.scoreB ?? 0),
    winnerId,
  });
}

export async function removeTeamFromTournament(
  tournamentId: string | undefined,
  teamId: string | undefined,
): Promise<void> {
  if (!tournamentId || !teamId) {
    throw new Error("TournamentId и TeamId обязательны для удаления команды");
  }
  await api.delete(`/tournaments/${tournamentId}/teams/${teamId}`);
}

export async function addTeamToTournament(
  tournamentId: string | undefined,
  teamId: string | undefined,
): Promise<void> {
  if (!tournamentId || !teamId) {
    throw new Error("TournamentId и TeamId обязательны для добавления команды");
  }
  await api.post(`/tournaments/${tournamentId}/teams`, { teamId });
}

export interface TournamentPermissionsDTO {
  canEditBracket: boolean;
}

export async function fetchTournamentPermissions(
  tournamentId: string | undefined,
): Promise<TournamentPermissionsDTO> {
  const { data } = await api.get<TournamentPermissionsDTO>(
    `/tournaments/${tournamentId ?? ""}/permissions`,
  );
  return data;
}

export type BracketSeedingStrategy = "random" | "rank" | "manual";

export type GenerateBracketOptions = {
  seedingStrategy?: BracketSeedingStrategy;
  manualOrder?: string[];
};

export type GenerateBracketResponse = {
  success: boolean;
  message?: string;
};

export async function generateBracket(
  tournamentId: string,
  options?: GenerateBracketOptions,
): Promise<GenerateBracketResponse> {
  const body =
    options && (options.seedingStrategy || (options.manualOrder?.length ?? 0) > 0)
      ? {
          seedingStrategy: options.seedingStrategy,
          manualOrder: options.manualOrder,
        }
      : undefined;

  const { data } = await api.post<GenerateBracketResponse>(
    `/tournaments/${tournamentId}/brackets/generate`,
    body,
  );
  return data;
}

export type RegenerateBracketPayload = {
  bracketType:
    | "SingleElimination"
    | "DoubleElimination"
    | "SwissSystem"
    | "RoundRobin"
    | "Swiss";
  enableThirdPlaceMatch: boolean;
  options?: GenerateBracketOptions;
};

export async function regenerateBracket(
  tournamentId: string,
  payload: RegenerateBracketPayload,
): Promise<GenerateBracketResponse> {
  const { data } = await api.post<GenerateBracketResponse>(
    `/tournaments/${tournamentId}/brackets/regenerate`,
    payload,
  );
  return data;
}

export async function freezeBracket(tournamentId: string): Promise<void> {
  await api.post(`/tournaments/${tournamentId}/brackets/freeze`);
}

export async function unfreezeBracket(tournamentId: string): Promise<void> {
  await api.post(`/tournaments/${tournamentId}/brackets/unfreeze`);
}

export async function publishBracket(tournamentId: string): Promise<void> {
  await api.post(`/tournaments/${tournamentId}/brackets/publish`);
}

export interface UpdateMatchResultRequest {
  team1Score: number;
  team2Score: number;
  winnerId?: string;
  expectedUpdatedAt: string;
  /** Тех. победа (неявка/дисквалификация) — помечает матч бейджем «тех.». */
  isTechnicalWin?: boolean;
}

export async function updateMatchResult(
  matchId: string,
  payload: UpdateMatchResultRequest,
): Promise<void> {
  await api.put(`/brackets/matches/${matchId}/result`, payload);
}

/**
 * Отменить (аннулировать) результат завершённого матча с откатом продвижения.
 * Доступно организатору/админу и только если следующий матч ещё не сыгран —
 * иначе backend вернёт 400 с понятным сообщением.
 */
export async function cancelMatchResult(matchId: string): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/cancel-result`);
}

/** KAN-167: исход заявки результата капитаном. */
export type CaptainResultStatus =
  | "Pending"
  | "Finalized"
  | "Disputed"
  | "ManualArbitration";

export interface CaptainResultResponse {
  status: CaptainResultStatus;
  winnerId?: string | null;
  team1Score: number;
  team2Score: number;
  disputeId?: string | null;
}

export interface SubmitCaptainResultRequest {
  winnerId: string;
  team1Score: number;
  team2Score: number;
}

/**
 * KAN-167: капитан команды заявляет/подтверждает результат матча. Первый заявитель →
 * Pending; вторая команда с тем же результатом → Finalized; с иным → Disputed
 * (бэкенд отвечает 409, но тело содержит status/disputeId — извлекаем из ошибки).
 */
export async function submitCaptainResult(
  matchId: string,
  payload: SubmitCaptainResultRequest,
): Promise<CaptainResultResponse> {
  try {
    const { data } = await api.post<CaptainResultResponse>(
      `/brackets/matches/${matchId}/captain-result`,
      payload,
    );
    return data;
  } catch (err) {
    // Расхождение результатов приходит как 409 с тем же телом (status/disputeId).
    const body = (err as { response?: { status?: number; data?: CaptainResultResponse } })?.response;
    if (body?.status === 409 && body.data?.status) return body.data;
    throw err;
  }
}

/** KAN-167: заявитель отзывает свой ещё не подтверждённый результат. */
export async function cancelCaptainResult(matchId: string): Promise<void> {
  await api.delete(`/brackets/matches/${matchId}/captain-result`);
}

export type { ApiMatchPendingResult };

export interface UpdateMatchScoreOnlyRequest {
  team1Score: number;
  team2Score: number;
  expectedUpdatedAt: string;
}

export async function updateMatchScoreOnly(
  matchId: string,
  payload: UpdateMatchScoreOnlyRequest,
): Promise<void> {
  await api.put(`/brackets/matches/${matchId}/score`, payload);
}

export interface SwapTeamsInMatchRequest {
  expectedUpdatedAt: string;
}

export async function swapTeamsInMatch(
  matchId: string,
  payload: SwapTeamsInMatchRequest,
): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/swap`, payload);
}

export interface MoveTeamToMatchRequest {
  teamId: string;
  fromMatchId: string;
  toMatchId: string;
  fromExpectedUpdatedAt: string;
  toExpectedUpdatedAt: string;
}

export async function moveTeamToMatch(
  payload: MoveTeamToMatchRequest,
): Promise<void> {
  await api.post(`/brackets/matches/move-team`, payload);
}

export interface SwapTeamsBetweenMatchesRequest {
  fromMatchId: string;
  fromSlot: "A" | "B";
  toMatchId: string;
  toSlot: "A" | "B";
  fromExpectedUpdatedAt: string;
  toExpectedUpdatedAt: string;
}

export async function swapTeamsBetweenMatches(
  payload: SwapTeamsBetweenMatchesRequest,
): Promise<void> {
  await api.post(`/brackets/matches/swap-between`, payload);
}

export async function reshuffleBracket(tournamentId: string): Promise<void> {
  await api.post(`/tournaments/${tournamentId}/brackets/reshuffle`);
}

export interface MatchLifecycleRequest {
  expectedUpdatedAt?: string;
}

export async function markMatchLive(
  matchId: string,
  payload?: MatchLifecycleRequest,
): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/live`, payload);
}

/**
 * Вернуть матч из эфира в Scheduled (KAN-371).
 *
 * Не то же, что пауза: пауза оставляет матч начатым, этот вызов снимает и время
 * старта — «матч не начинался». Матч с результатом бэкенд откатывать откажется,
 * для него есть отмена результата.
 */
export async function unliveMatch(
  matchId: string,
  payload?: MatchLifecycleRequest,
): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/unlive`, payload);
}

export async function pauseMatch(
  matchId: string,
  payload?: MatchLifecycleRequest,
): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/pause`, payload);
}

export async function resumeMatch(
  matchId: string,
  payload?: MatchLifecycleRequest,
): Promise<void> {
  await api.post(`/brackets/matches/${matchId}/resume`, payload);
}
